import $ from 'jquery';
import debounce from 'just-debounce';
import Flickity from 'flickity';
import * as breakpoint from '@pixelunion/breakpoint';
import LazyLoader from '../helpers/LazyLoader';

export default class DynamicInstagramFeed {
  constructor(section) {
    this.settings = section.data;
    this.onboarding = this.settings.onboarding;
    this.photoCount = this.settings.photo_count;
    this.accessToken = this.settings.access_token;
    this.$el = $(section.el);

    this._load = this._load.bind(this);

    this.lazyLoader = new LazyLoader(section.el, this._load);
  }

  _load() {
    this.$window = $(window);
    this.$photoContainer = this.$el.find('[data-instagram-content]');
    this.$photoContainerPlaceholder = this.$el.find('[data-instagram-photo-placeholder]');

    this.$photoTemplate = $('<figure class="instagram--photo" data-instagram-photo />');

    // Activate flickity on mobile
    this._mobileSlider = this._mobileSlider.bind(this);
    breakpoint.onChange(this._mobileSlider);
    this._mobileSlider();

    if (this.onboarding || !this.accessToken) {
      return;
    }

    this._getPhotos()
      .done((photos) => this._renderPhotos(photos))
      .fail((error) => {
        console.warn(`Instagram: ${error}`);
      });
  }

  _getPhotos() {
    const url = `https://api.instagram.com/v1/users/self/media/recent?access_token=${this.accessToken}&count=${this.photoCount}&callback=`;
    const photoPromise = $.ajax(url, {
      dataType: 'jsonp',
    });

    return photoPromise.then((response) => {
      if (response.meta.code !== 200) {
        const error = `${response.meta.error_message} (${response.meta.error_type})`;
        return $.Deferred().reject(error).promise();
      }

      return response.data;
    });
  }

  _renderPhotos(photos) {
    const photoArray = [];
    let photoIndex = 0;
    photos.forEach((photo) => {
      photoIndex += 1;
      const $template = this.$photoTemplate.clone();
      const lowResolution = photo.images.low_resolution;
      const standardResolution = photo.images.standard_resolution;
      let imageURL = lowResolution.url;

      if (window.devicePixelRatio >= 2) {
        imageURL = standardResolution.url;
      }

      const $link = $(`<a aria-label="view photo ${photoIndex} by ${photo.user.username} on instagram" target="_blank" href="${photo.link}"></a>`);
      const $photo = $(`
        <div
          class="instagram--photo-img"
          role="img"
          style="background-image: url(${imageURL});"
          aria-label="photo ${photoIndex} by ${photo.user.username} on instagram"
        >
      `);

      $link.append($photo);
      $template.append($link);

      photoArray.push($template);
    });

    this.$photoContainerPlaceholder.fadeOut(() => {
      this._destroyFlickity();
      this.$photoContainer.append(photoArray);
      this.$photoContainerPlaceholder.remove();
      this._mobileSlider();
    });
  }


  _initFlickity() {
    this.flickity = new Flickity(this.$photoContainer[0], {
      cellSelector: '.instagram--photo',
      contain: true,
      freeScroll: true,
      percentPosition: false,
      prevNextButtons: false,
      pageDots: false,
      setGallerySize: false,
    });

    this._bindSlider();
  }

  _destroyFlickity() {
    if (!this.flickity) {
      return;
    }

    this.$window.off('.instagram-feed');
    this.$photoContainer.off('.instagram-feed');
    this.flickity.destroy();
    this.flickity = null;
  }

  _mobileSlider() {
    // If is Large layout, attempt to destroy flickity
    if (breakpoint.min('M')) {
      this._destroyFlickity();
      return;
    }

    // Is XS/S/M, and flickity is init'd -- do nothing
    if (this.flickity) {
      return;
    }

    // Is XS/S/M, and flickity is not init'd
    this._initFlickity();
  }

  _bindSlider() {
    const $slider = this.$photoContainer.find('.flickity-slider');

    this.$window.on('resize.instagram-feed', debounce(() => {
      this.$photoContainer.trigger('heightUpdate.instagram-feed');
    }));

    this.flickity.on('cellSelect', () => {
      this.$photoContainer.trigger('heightUpdate.instagram-feed');
    });

    this.$photoContainer.on('heightUpdate.instagram-feed', () => {
      if (!this.flickity) {
        return;
      }

      $slider.height(Math.ceil(this.flickity.maxCellHeight));
    });

    // Sets the Slider to the height of the first slide
    this.$photoContainer.trigger('heightUpdate.instagram-feed');
  }

  onSectionUnload() {
    this.lazyLoader.unload();
  }
}
